import React, { useState, useEffect } from 'react';
import { Plus, Minus } from 'lucide-react';
import { addons as addonsApi } from '../utils/api';
import { toast } from 'react-toastify';

const AddOnsSelector = ({ selectedAddons, onAddonsChange, label = 'Add Popular Extras' }) => {
  const [addons, setAddons] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    // Initialize quantities from selectedAddons
    const initialQuantities = {};
    selectedAddons.forEach(addon => {
      initialQuantities[addon.id] = addon.quantity || 1;
    });
    setQuantities(initialQuantities);
  }, [selectedAddons]);

  useEffect(() => {
    // Fetch add-ons from API
    const loadAddons = async () => {
      try {
        const response = await addonsApi.getAll();
        setAddons(response.data);
      } catch (error) {
        console.error('Failed to load add-ons:', error);
        toast.error('Failed to load add-ons');
      } finally {
        setLoading(false);
      }
    };
    
    loadAddons();
  }, []);

  const isSelected = (addonId) => {
    return selectedAddons.some(a => a.id === addonId);
  };

  const getQuantity = (addonId) => {
    return quantities[addonId] || 0;
  };

  const handleToggle = (addon) => {
    if (isSelected(addon.id)) {
      // Remove addon
      onAddonsChange(selectedAddons.filter(a => a.id !== addon.id));
      const newQuantities = { ...quantities };
      delete newQuantities[addon.id];
      setQuantities(newQuantities);
    } else {
      // Add addon with quantity 1
      onAddonsChange([...selectedAddons, { ...addon, quantity: 1 }]);
      setQuantities({ ...quantities, [addon.id]: 1 });
    }
  };

  const handleQuantityChange = (addonId, delta) => {
    const currentQty = getQuantity(addonId);
    const newQty = Math.max(0, currentQty + delta);
    
    if (newQty === 0) {
      // Remove addon
      onAddonsChange(selectedAddons.filter(a => a.id !== addonId));
      const newQuantities = { ...quantities };
      delete newQuantities[addonId];
      setQuantities(newQuantities);
    } else {
      // Update quantity
      setQuantities({ ...quantities, [addonId]: newQty });
      onAddonsChange(selectedAddons.map(a => 
        a.id === addonId ? { ...a, quantity: newQty } : a
      ));
    }
  };

  const getTotalPrice = () => {
    return selectedAddons.reduce((sum, addon) => {
      return sum + (parseFloat(addon.price) * (addon.quantity || 1));
    }, 0);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  if (addons.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No add-ons available at this time.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{label}</h3>
        {selectedAddons.length > 0 && (
          <div className="text-primary-600 font-semibold">
            +${getTotalPrice().toFixed(2)}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {addons
          .slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE)
          .map((addon) => {
            const selected = isSelected(addon.id);
            const quantity = getQuantity(addon.id);

            return (
              <div
                key={addon.id}
                className={`border-2 rounded-lg p-4 transition-all ${
                  selected
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => handleToggle(addon)}
                        className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                      />
                      <div>
                        <h4 className="font-semibold text-gray-900">{addon.name}</h4>
                        <p className="text-sm text-gray-600">{addon.description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 ml-4">
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">
                        ${parseFloat(addon.price).toFixed(2)}
                      </div>
                      {selected && quantity > 1 && (
                        <div className="text-xs text-gray-500">
                          ${(parseFloat(addon.price) * quantity).toFixed(2)} total
                        </div>
                      )}
                    </div>

                    {selected && (
                      <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(addon.id, -1)}
                          className="p-1 hover:bg-gray-100 rounded-l-lg transition-colors"
                        >
                          <Minus size={16} />
                        </button>
                        <span className="px-3 font-semibold min-w-[2rem] text-center">
                          {quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(addon.id, 1)}
                          className="p-1 hover:bg-gray-100 rounded-r-lg transition-colors"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Pagination Controls */}
      {addons.length > ITEMS_PER_PAGE && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>←</span>
            <span>Previous</span>
          </button>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              Page {currentPage + 1} of {Math.ceil(addons.length / ITEMS_PER_PAGE)}
            </span>
            <span className="text-xs text-gray-500">
              ({addons.length} total)
            </span>
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(Math.ceil(addons.length / ITEMS_PER_PAGE) - 1, currentPage + 1))}
            disabled={currentPage >= Math.ceil(addons.length / ITEMS_PER_PAGE) - 1}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Next</span>
            <span>→</span>
          </button>
        </div>
      )}

      {selectedAddons.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-700">Add-ons Total:</span>
            <span className="text-xl font-bold text-primary-600">
              ${getTotalPrice().toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOnsSelector;
